import { useRef, useState } from "react";
import { router, useForm, usePage } from "@inertiajs/react";
import { Heart, Clock } from "lucide-react";
import PretendCheckout from "@/components/checkout/PretendCheckout";
import SquareCheckout from "@/components/checkout/SquareCheckout";
import FormField from "@/components/forms/FormField";
import PrivacyConsentCheckbox from "@/components/forms/PrivacyConsentCheckbox";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Notice } from "@/components/ui/notice";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { formatMoney } from "@/lib/format";
import { cn } from "@/lib/utils";

// BUSINESS_RULES §8: $5 / $10 / $25 / $50 / $100, or a custom amount.
const PRESETS = [500, 1000, 2500, 5000, 10000];

// The fee that, added to the donation, leaves the school the full amount
// after the card processor takes its cut. Same gross-up as
// Payments::PricingPolicy.fee_for, from the schedule the server sends.
function coveredFeeCents(amountCents, schedule) {
  if (!schedule || amountCents <= 0) return 0;
  return Math.ceil((amountCents + schedule.fixed_cents) / (1 - schedule.percent)) - amountCents;
}

// What the processor takes when the donor doesn't cover it.
function processorFeeCents(amountCents, schedule) {
  if (!schedule || amountCents <= 0) return 0;
  return Math.round(amountCents * schedule.percent + schedule.fixed_cents);
}

/**
 * The donation form on the public student and school pages. Server props
 * decide whether it's open (`donationState`: open | not_open_yet |
 * finished | not_set_up); a charge still being confirmed swaps the form
 * for a note so nobody pays twice. The demo school sets `pretendPayments`
 * to swap Square's card form for a pretend one.
 */
export default function DonationCard({
  school,
  donatePath,
  donationState,
  donationsOpenOn,
  feeSchedule,
  square,
  pretendPayments = false,
  title = "Make a donation",
  description,
  messagePlaceholder = "Keep reading!",
  id = "donate",
}) {
  const { flash, errors: pageErrors = {} } = usePage().props;

  if (flash?.payment_pending) {
    return (
      <Card id={id} className="border-info/30 bg-info/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-info" />
            We're confirming your payment
          </CardTitle>
          <CardDescription className="leading-relaxed">{flash.payment_pending}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (donationState !== "open") {
    const message = {
      not_open_yet: donationsOpenOn ? `Donations open ${donationsOpenOn}.` : "Donations aren't open yet.",
      finished: "This read-a-thon is wrapped up. Thank you to everyone who gave.",
      not_set_up: `${school.name} is still getting online donations ready. Check back soon.`,
    }[donationState];
    return (
      <Card id={id}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-muted-foreground" />
            {{ finished: "Donations are closed", not_set_up: "Donations aren't set up" }[donationState] || "Not open yet"}
          </CardTitle>
          <CardDescription>{message}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <OpenDonationForm
      id={id}
      school={school}
      donatePath={donatePath}
      feeSchedule={feeSchedule}
      square={square}
      pretendPayments={pretendPayments}
      title={title}
      description={description}
      messagePlaceholder={messagePlaceholder}
      pageErrors={pageErrors}
      alert={flash?.alert}
    />
  );
}

function OpenDonationForm({ id, school, donatePath, feeSchedule, square, pretendPayments, title, description, messagePlaceholder, pageErrors, alert }) {
  const { data, setData } = useForm({
    amount_cents: 5000,
    donor_name: "",
    donor_email: "",
    donor_message: "",
    anonymous: false,
    fee_covered: true,
    privacy_consent: false,
  });
  const [customMode, setCustomMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cardError, setCardError] = useState(null);
  const [localErrors, setLocalErrors] = useState({});
  const checkoutRef = useRef(null);
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const errorFor = (k) => localErrors[k] || (Array.isArray(pageErrors[k]) ? pageErrors[k][0] : pageErrors[k]) || null;

  const fee = coveredFeeCents(data.amount_cents, feeSchedule);
  const total = data.amount_cents + (data.fee_covered ? fee : 0);
  const schoolGets = data.fee_covered ? data.amount_cents : data.amount_cents - processorFeeCents(data.amount_cents, feeSchedule);

  const validate = () => {
    const errors = {};
    if (data.amount_cents < 100) errors.amount = "Choose an amount of at least $1.";
    if (!data.donor_name.trim()) errors.donor_name = "Add your name for the receipt.";
    if (!data.donor_email.trim()) errors.donor_email = "Add your email so we can send your receipt.";
    if (!data.privacy_consent) errors.privacy_consent = "Please confirm you've read the Privacy Policy.";
    setLocalErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting || !validate()) return;
    setSubmitting(true);
    setCardError(null);

    try {
      const clientData = await checkoutRef.current?.tokenize();
      if (!clientData) {
        setSubmitting(false);
        return;
      }
      router.post(donatePath, { ...data, ...clientData, idempotency_key: idempotencyKey }, {
        preserveScroll: false,
        onFinish: () => setSubmitting(false),
      });
    } catch (err) {
      console.error("Donation submit error:", err);
      setCardError(err.message || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <Card id={id} className="scroll-mt-6 border-primary/30 bg-primary/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Heart className="h-5 w-5 text-primary" />
          {title}
        </CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} noValidate className="space-y-5">
          <fieldset>
            <legend className="mb-2 text-sm font-medium text-foreground">Amount</legend>
            <div className="grid grid-cols-3 gap-2">
              {PRESETS.map((cents) => (
                <AmountButton key={cents} selected={!customMode && data.amount_cents === cents} onClick={() => { setCustomMode(false); setData("amount_cents", cents); }}>
                  {formatMoney(cents)}
                </AmountButton>
              ))}
              <AmountButton selected={customMode} onClick={() => { setCustomMode(true); setData("amount_cents", 0); }}>
                Other
              </AmountButton>
            </div>
            {customMode && (
              <div className="mt-3">
                <FormField
                  name="amount_dollars"
                  label="Amount in dollars (CAD)"
                  type="number"
                  inputMode="decimal"
                  min="1"
                  step="1"
                  autoFocus
                  value={data.amount_cents ? data.amount_cents / 100 : ""}
                  onChange={(e) => setData("amount_cents", Math.round(parseFloat(e.target.value || "0") * 100))}
                />
              </div>
            )}
            {errorFor("amount") && <p className="mt-1.5 text-sm text-destructive">{errorFor("amount")}</p>}
          </fieldset>

          <FormField
            name="donor_name"
            label="Your name"
            required
            autoComplete="name"
            value={data.donor_name}
            onChange={(e) => setData("donor_name", e.target.value)}
            error={errorFor("donor_name")}
          />
          <FormField
            name="donor_email"
            label="Your email"
            type="email"
            required
            autoComplete="email"
            value={data.donor_email}
            onChange={(e) => setData("donor_email", e.target.value)}
            error={errorFor("donor_email")}
            help="For your receipt. It isn't shown on the page."
          />
          <FormField
            name="donor_message"
            label="Message (optional)"
            placeholder={messagePlaceholder}
            value={data.donor_message}
            onChange={(e) => setData("donor_message", e.target.value)}
            error={errorFor("donor_message")}
          />
          <label className="flex cursor-pointer items-start gap-3 text-sm">
            <Checkbox className="mt-0.5" checked={data.anonymous} onChange={(e) => setData("anonymous", e.target.checked)} />
            <span className="leading-snug text-muted-foreground">Show my name as Anonymous on this page</span>
          </label>

          <fieldset>
            <legend className="mb-2 text-sm font-medium text-foreground">Card details</legend>
            {pretendPayments ? (
              <PretendCheckout ref={checkoutRef} />
            ) : square && (
              <SquareCheckout
                ref={checkoutRef}
                clientConfig={square}
                totalCents={total}
                donorName={data.donor_name}
                donorEmail={data.donor_email}
                onError={setCardError}
              />
            )}
            {cardError && <p className="mt-1.5 text-sm text-destructive" role="alert">{cardError}</p>}
          </fieldset>

          <label className="flex cursor-pointer items-start gap-3 text-sm">
            <Checkbox className="mt-0.5" checked={data.fee_covered} onChange={(e) => setData("fee_covered", e.target.checked)} />
            <span className="leading-snug text-muted-foreground">
              Add {formatMoney(fee, { cents: true })} to cover the card fee
            </span>
          </label>

          <dl className="space-y-1.5 rounded-lg border border-primary/20 bg-card p-3 text-sm font-tabular">
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Donation</dt>
              <dd className="text-foreground">{formatMoney(data.amount_cents, { cents: true })}</dd>
            </div>
            {data.fee_covered && (
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Card fee</dt>
                <dd className="text-foreground">{formatMoney(fee, { cents: true })}</dd>
              </div>
            )}
            <div className="flex justify-between border-t border-border pt-1.5 font-semibold">
              <dt className="text-foreground">Total</dt>
              <dd className="text-foreground">{formatMoney(total, { cents: true })}</dd>
            </div>
            {data.amount_cents >= 100 && (
              <p className="pt-1 text-xs text-muted-foreground">
                {data.fee_covered
                  ? `${school.name} gets the full ${formatMoney(data.amount_cents, { cents: true })}.`
                  : `${school.name} gets about ${formatMoney(Math.max(schoolGets, 0), { cents: true })} after the card fee.`}
              </p>
            )}
          </dl>

          <PrivacyConsentCheckbox
            checked={data.privacy_consent}
            onChange={(checked) => setData("privacy_consent", checked)}
            error={errorFor("privacy_consent")}
            id="donation_privacy_consent"
          />

          {alert && <Notice tone="destructive">{alert}</Notice>}

          <Button type="submit" size="lg" className="w-full" disabled={submitting}>
            {submitting ? "Processing…" : `Donate ${formatMoney(total, { cents: true })}`}
          </Button>

          <p className="text-xs leading-relaxed text-muted-foreground">
            {pretendPayments
              ? "This is a demo school, so donations here go straight to a pretend receipt."
              : `Secured by Square. Your donation goes straight to ${school.name}'s Square account; PagePledge never holds the money. You'll get an emailed receipt for your records, not a charitable tax receipt.`}
          </p>
        </form>
      </CardContent>
    </Card>
  );
}

function AmountButton({ selected, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        "min-h-11 rounded-lg border px-3 text-sm font-semibold font-tabular transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        selected ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground hover:bg-primary/10"
      )}
    >
      {children}
    </button>
  );
}
